import { useCallback, useEffect, useRef, useState } from "react";

import type { ExtendedUIMessage } from "../chat.types";

export interface UseChatScrollControlsOptions {
  messages: ExtendedUIMessage[];
}

export const useChatScrollControls = ({ messages }: UseChatScrollControlsOptions) => {
  // The three observed elements are held in state, set through callback refs, rather than in plain
  // ref objects. The surface that renders them is not stable: QWIB's compact playbook mode swaps the
  // transcript for a "Playbook running" card and back, and every swap mounts a NEW scroller,
  // transcript, and footer under this same provider. A ref object would silently keep pointing the
  // resize observers at the detached originals, so the remounted transcript sat at `scrollTop = 0`
  // and never followed the tail again (TP-992). State re-runs the observer effect on every swap.
  const [scrollableElement, setScrollableElement] = useState<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const [footerElement, setFooterElement] = useState<HTMLDivElement | null>(null);

  // Scroll reads and writes go through this ref so the callbacks below stay identity-stable.
  const scrollableElementRef = useRef<HTMLDivElement | null>(null);

  const scrollableRef = useCallback((element: HTMLDivElement | null) => {
    scrollableElementRef.current = element;
    setScrollableElement(element);
  }, []);

  // Programmatic scroll tracking
  const isProgrammaticScrollRef = useRef(false);
  const lastScrollTopRef = useRef(0);

  // State for auto-scroll behavior
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  const scrollToBottom = useCallback(() => {
    const scrollableElement = scrollableElementRef.current;
    if (!scrollableElement) {
      return;
    }

    // Mark this as programmatic scrolling
    isProgrammaticScrollRef.current = true;

    const maxScrollHeight = scrollableElement.scrollHeight - scrollableElement.clientHeight;
    scrollableElement.scrollTop = maxScrollHeight;

    // Update last scroll position for direction detection
    lastScrollTopRef.current = scrollableElement.scrollTop;

    // Reset the flag after a brief delay to allow the scroll event to fire
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 100);
  }, []);

  const isScrolledToBottom = useCallback((element: HTMLDivElement): boolean => {
    const maxScrollHeight = element.scrollHeight - element.clientHeight;
    const tolerance = 50; // Increased tolerance for better detection
    return Math.abs(element.scrollTop - maxScrollHeight) <= tolerance;
  }, []);

  const handleScroll = useCallback(() => {
    // Ignore scroll events from programmatic scrolling
    if (isProgrammaticScrollRef.current) {
      return;
    }

    const scrollableElement = scrollableElementRef.current;
    if (!scrollableElement || !messages?.length) {
      return;
    }

    const currentScrollTop = scrollableElement.scrollTop;
    const isScrollingUp = currentScrollTop < lastScrollTopRef.current;

    // Update last scroll position for next comparison
    lastScrollTopRef.current = currentScrollTop;

    // Only disable auto-scroll if user is scrolling up
    if (isScrollingUp) {
      setShouldAutoScroll(false);
    } else if (isScrolledToBottom(scrollableElement)) {
      setShouldAutoScroll(true);
    }
  }, [messages, isScrolledToBottom]);

  const resumeAutoScroll = useCallback(() => {
    setShouldAutoScroll(true);
    scrollToBottom();
  }, [scrollToBottom]);

  // `shouldAutoScroll` is read through a ref by the coalesced resize flush below, so the
  // scheduled frame honors whether the user is scrolled up AT FLUSH TIME rather than whatever
  // was captured when the burst started.
  const shouldAutoScrollRef = useRef(shouldAutoScroll);
  shouldAutoScrollRef.current = shouldAutoScroll;

  // Three elements are observed (the scroller, the transcript content, and the footer), and one
  // layout change — a streamed token growing the transcript — typically resizes more than one of
  // them in the same frame. Each `onResize` used to run `scrollToBottom` synchronously, so a single
  // change forced up to three `scrollHeight`/`clientHeight` reads and three scroll writes. Collapse
  // a burst into ONE animation-frame update instead; follow-tail behavior is unchanged because the
  // flush performs the same `resumeAutoScroll` the synchronous path did.
  const resizeFrameRef = useRef<number | null>(null);

  const handleResize = useCallback(() => {
    if (resizeFrameRef.current !== null) {
      return;
    }

    resizeFrameRef.current = requestAnimationFrame(() => {
      resizeFrameRef.current = null;

      if (!shouldAutoScrollRef.current) {
        return;
      }

      resumeAutoScroll();
    });
  }, [resumeAutoScroll]);

  useEffect(
    () => () => {
      if (resizeFrameRef.current !== null) {
        cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    },
    []
  );

  // A newly mounted scroller starts at the top. It is a fresh transcript — whatever position the
  // user held in the previous one is gone with it — so open it on the latest message and resume
  // following the tail, the same as switching sessions does.
  useEffect(() => {
    if (scrollableElement) {
      resumeAutoScroll();
    }
  }, [scrollableElement, resumeAutoScroll]);

  // ONE observer for all three elements, re-created whenever any of them is swapped for a new node.
  // `observe` reports each element's initial size, so a remounted transcript also gets a flush once
  // its content has laid out.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(handleResize);
    for (const element of [scrollableElement, contentElement, footerElement]) {
      if (element) {
        observer.observe(element);
      }
    }

    return () => observer.disconnect();
  }, [scrollableElement, contentElement, footerElement, handleResize]);

  return {
    handleScroll,
    resumeAutoScroll,
    shouldAutoScroll,
    scrollableRef,
    contentRef: setContentElement,
    footerRef: setFooterElement,
  };
};
